// 004 (FR-019–FR-026, FR-044, FR-049–FR-054; US2-3..5, US4, US6): edit a child's name, class and
// section (no schoolCode is sent for that), Change school or, for a child not linked to a school, Link
// with school code (both through ChangeSchoolFlow), or remove the child after confirmation. A saved
// class the school no longer offers is shown but must be picked again. Opened directly (web
// reload) it loads the child with getMyStudent; 404 shows a not-found state.
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { View } from "react-native";

import { type AppError, isAppError } from "@/api/errors";
import type { Api } from "@/api/types";
import { appNotices } from "@/shell/notice/store";
import { StateView } from "@/shell/StateView";
import { useNetworkStatus } from "@/shell/useNetworkStatus";
import { Button } from "@/ui/Button";
import { InlineMessage } from "@/ui/InlineMessage";
import { Text } from "@/ui/Text";
import { TextField } from "@/ui/TextField";

import { ClassSectionPickers, type ClassSection } from "../ClassSectionPickers";
import { PROFILE_COPY, profileErrorMessage } from "../copy";
import { useProfileClient } from "../profileApi";
import { Card, OfflineNote, ProfileFrame, useProfileStyles } from "../ui";
import { useParentData } from "../useParentData";
import { validateName } from "../validation";
import { standardClassName } from "../viewModels";
import { ChangeSchoolFlow } from "./ChangeSchoolFlow";
import { useRemoveChild } from "./useRemoveChild";

type Load =
  | { s: "loading" }
  | { s: "ready"; student: Api["Student"] }
  | { s: "notFound" }
  | { s: "error"; error: AppError };

const UNKNOWN: AppError = {
  kind: "unknown",
  status: null,
  code: null,
  requestId: null,
  fieldErrors: [],
  retryAfterSeconds: null,
  allowedClients: null,
};

function startValue(st: Api["Student"]): ClassSection {
  const classes = st.school?.classes ?? [];
  const cls = classes.find((c) => c.classLevel === st.classLevel);
  if (!cls) return { classLevel: null, section: null };
  return {
    classLevel: cls.classLevel,
    section: cls.sections.includes(st.section ?? "") ? st.section : null,
  };
}

function EditForm({
  student,
  reload,
}: {
  student: Api["Student"];
  reload: () => Promise<Api["Student"] | null>;
}) {
  const styles = useProfileStyles();
  const router = useRouter();
  const { online } = useNetworkStatus();
  const { actions } = useParentData();
  const [name, setName] = useState(student.name);
  const [value, setValue] = useState<ClassSection>(() => startValue(student));
  const [classes, setClasses] = useState(student.school?.classes ?? []);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [changing, setChanging] = useState(false);
  const [lostLink, setLostLink] = useState(false);
  // Back to the Profile it was opened from (else open Profile), so screens do not pile up.
  const toProfile = () =>
    router.canGoBack() ? router.back() : router.replace("/profile");
  const remove = useRemoveChild(toProfile);
  const schoolName = student.school?.name ?? "";
  const classGone =
    student.classLevel !== null &&
    !classes.some((c) => c.classLevel === student.classLevel);

  async function save() {
    if (pending || !online) return;
    const nameError = validateName(name);
    const next = {
      name: nameError ?? undefined,
      classLevel: value.classLevel ? undefined : PROFILE_COPY.chooseClass,
      section: value.section ? undefined : PROFILE_COPY.chooseSection,
    };
    setErrors(next);
    setFormError(null);
    if (next.name || next.classLevel || next.section) return;
    const body: Api["StudentUpdate"] = {};
    if (name.trim() !== student.name) body.name = name.trim();
    if (
      value.classLevel !== student.classLevel ||
      value.section !== student.section
    ) {
      body.classLevel = value.classLevel as Api["ClassLevelCode"];
      body.section = value.section!;
    }
    if (Object.keys(body).length === 0) {
      toProfile();
      return;
    }
    setPending(true);
    try {
      await actions.updateStudent(student.id, body);
      appNotices.post(PROFILE_COPY.childSaved(name.trim()));
      toProfile();
    } catch (e) {
      if (!isAppError(e)) throw e;
      if (e.fieldErrors.some((f) => f.code === "NOT_IN_SCHOOL_CLASSES")) {
        // The school's list changed: reload it, clear the choice and ask again (FR-044).
        const fresh = await reload();
        setClasses(fresh?.school?.classes ?? []);
        setValue({ classLevel: null, section: null });
        setErrors({ classLevel: PROFILE_COPY.classGone });
      } else if (e.code === "STUDENT_NOT_LINKED") {
        // A school admin unlinked the child meanwhile: say so and reload (FR-052).
        setLostLink(true);
        void actions.refreshStudents();
        await reload();
      } else {
        const msg = profileErrorMessage(e, {
          action: "edit",
          child: name.trim(),
          school: schoolName,
        });
        setErrors(msg.fields ?? {});
        setFormError(msg.form ?? null);
      }
    } finally {
      setPending(false);
    }
  }

  if (!student.linked || student.school === null) {
    return (
      <ChangeSchoolFlow
        student={student}
        mode="link"
        intro={lostLink ? PROFILE_COPY.notLinkedAny(student.name) : null}
        onDone={toProfile}
        onCancel={toProfile}
        onEditClassSection={toProfile}
      />
    );
  }

  if (changing) {
    return (
      <ChangeSchoolFlow
        student={student}
        mode="move"
        onDone={toProfile}
        onCancel={() => setChanging(false)}
        onEditClassSection={() => setChanging(false)}
      />
    );
  }

  return (
    <Card testID="edit-child">
      <Text tone="muted">{schoolName}</Text>
      <TextField
        label={PROFILE_COPY.name}
        value={name}
        onChangeText={setName}
        kind="name"
        error={errors.name}
      />
      {classGone && student.classLevel ? (
        <Text tone="muted">{`Saved class: ${standardClassName(student.classLevel)} (no longer offered). Choose the class again.`}</Text>
      ) : null}
      <ClassSectionPickers
        classes={classes}
        schoolName={schoolName}
        value={value}
        onChange={setValue}
        errors={{ classLevel: errors.classLevel, section: errors.section }}
        disabled={!online}
      />
      {formError ? (
        <InlineMessage tone="error">{formError}</InlineMessage>
      ) : null}
      <OfflineNote online={online} />
      <View style={styles.buttons}>
        <Button variant="outline" onPress={toProfile}>
          {PROFILE_COPY.cancel}
        </Button>
        <Button
          onPress={() => void save()}
          pending={pending}
          disabled={!online}
        >
          {PROFILE_COPY.save}
        </Button>
      </View>
      {student.hasOpenOrder ? (
        <Text tone="muted">{PROFILE_COPY.openOrderMove(student.name)}</Text>
      ) : null}
      <Button
        variant="outline"
        disabled={!online || student.hasOpenOrder}
        onPress={() => setChanging(true)}
      >
        {PROFILE_COPY.changeSchool}
      </Button>
      {student.hasOpenOrder ? (
        <Text tone="muted">{PROFILE_COPY.openOrderRemove(student.name)}</Text>
      ) : null}
      {remove.error?.text ? (
        <InlineMessage tone="error">{remove.error.text}</InlineMessage>
      ) : null}
      <Button
        variant="outline"
        disabled={!online || student.hasOpenOrder}
        onPress={() => remove.ask({ id: student.id, name: student.name })}
      >
        {PROFILE_COPY.removeChild(student.name)}
      </Button>
      {remove.dialog}
    </Card>
  );
}

export function EditChildScreen() {
  const params = useLocalSearchParams<{ studentId?: string }>();
  const studentId =
    typeof params.studentId === "string" ? params.studentId : "";
  const router = useRouter();
  const client = useProfileClient();
  const { students } = useParentData();
  const cached = students.data?.find((s) => s.id === studentId) ?? null;
  const [load, setLoad] = useState<Load>(() =>
    cached ? { s: "ready", student: cached } : { s: "loading" },
  );

  const loadOnce = useCallback(
    (): Promise<Load> =>
      client.getStudent(studentId).then(
        (student): Load => ({ s: "ready", student }),
        (e: unknown): Load =>
          isAppError(e) && e.kind === "notFound"
            ? { s: "notFound" }
            : { s: "error", error: isAppError(e) ? e : UNKNOWN },
      ),
    [client, studentId],
  );

  const needsLoad = load.s === "loading";
  useEffect(() => {
    if (!needsLoad) return;
    let live = true;
    void loadOnce().then((next) => {
      if (live) setLoad(next);
    });
    return () => {
      live = false;
    };
  }, [needsLoad, loadOnce]);

  const fetchStudent = async (): Promise<Api["Student"] | null> => {
    const next = await loadOnce();
    setLoad(next);
    return next.s === "ready" ? next.student : null;
  };

  let body;
  if (load.s === "ready")
    body = <EditForm student={load.student} reload={fetchStudent} />;
  else if (load.s === "notFound")
    body = (
      <Card testID="edit-child-not-found">
        <Text>{PROFILE_COPY.notFoundChild}</Text>
        <Button variant="outline" onPress={() => router.replace("/profile")}>
          {PROFILE_COPY.backToProfile}
        </Button>
      </Card>
    );
  else if (load.s === "error")
    body = (
      <StateView
        status="error"
        error={load.error}
        onRetry={() => {
          setLoad({ s: "loading" });
        }}
      />
    );
  else body = <StateView status="loading" />;

  const title =
    load.s === "ready" ? PROFILE_COPY.editChild(load.student.name) : "";
  return (
    <ProfileFrame testID="edit-child-screen" narrow>
      {title ? (
        <Text variant="display" accessibilityRole="header" aria-level={1}>
          {title}
        </Text>
      ) : null}
      {body}
    </ProfileFrame>
  );
}
